/**
 * @module utils/auditExport
 * @description Exportadores del trail de auditoría a CSV y PDF.
 *
 * Permite a auditores y administradores descargar el historial completo de cambios
 * de un control ISO en formato tabular para análisis externo o evidencia documental.
 *
 * ## Formatos
 * - `exportAuditToCSV()`: CSV con encoding UTF-8, apto para Excel y herramientas GRC
 * - `exportAuditToPDF()`: PDF con tabla formateada usando jsPDF + autotable
 *
 * Los campos incluidos: fecha, usuario, email, acción, tipo, valor anterior/nuevo,
 * usuarios afectados, archivos subidos y cambio de progreso porcentual.
 *
 * @see AuditTrail component que invoca estas funciones desde el DetailPanel
 * @see AuditEntry en types/iso.ts para la estructura de datos de entrada
 */
import type { AuditEntry } from '../types/iso';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Exporta audit trail a formato CSV
 * @param auditEntries - Array de entradas de auditoría
 * @param controlCode - Código del control (opcional, para el nombre del archivo)
 * @param controlTitle - Título del control (opcional, para el encabezado)
 */
export function exportAuditToCSV(
  auditEntries: AuditEntry[],
  controlCode?: string,
  controlTitle?: string
): void {
  // 1. Definir encabezados del CSV
  const headers = [
    'Fecha',
    'Usuario',
    'Email',
    'Acción',
    'Tipo',
    'Valor Anterior',
    'Valor Nuevo',
    'Usuarios Afectados',
    'Archivos Subidos',
    'Cambio de Progreso (%)'
  ];

  // 2. Convertir entradas a filas CSV
  const rows = auditEntries.map(entry => {
    const fecha = new Date(entry.timestamp).toLocaleString('es-ES');
    const usuario = entry.user.name;
    const email = entry.user.email;
    const accion = entry.action;
    const tipo = entry.actionType;
    const valorAnterior = entry.previousValue ? JSON.stringify(entry.previousValue) : '';
    const valorNuevo = entry.newValue ? JSON.stringify(entry.newValue) : '';
    const usuariosAfectados = entry.affectedUsers?.map(u => u.name).join(', ') || '';
    const archivosSubidos = entry.filesUploaded?.map(f => f.name).join(', ') || '';
    const cambioProgreso = entry.impactMetrics?.completionChange || '';

    return [
      fecha,
      usuario,
      email,
      accion,
      tipo,
      valorAnterior,
      valorNuevo,
      usuariosAfectados,
      archivosSubidos,
      cambioProgreso
    ];
  });

  // 3. Construir CSV
  const csvContent = [
    // Encabezado opcional con info del control
    controlCode && controlTitle ? [`# Auditoría - ${controlCode}: ${controlTitle}`] : [],
    [`# Exportado el ${new Date().toLocaleString('es-ES')}`],
    [''], // Línea vacía
    headers,
    ...rows
  ]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  // 4. Crear archivo y descargar
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `auditoria_${controlCode || 'completa'}_${Date.now()}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Exporta audit trail completo del sistema a CSV
 * @param auditEntriesByControl - Mapa de audit entries agrupados por control
 */
export function exportCompleteAuditToCSV(
  auditEntriesByControl: Map<string, { code: string; title: string; entries: AuditEntry[] }>
): void {
  // 1. Definir encabezados
  const headers = [
    'Control',
    'Fecha',
    'Usuario',
    'Email',
    'Acción',
    'Tipo',
    'Valor Anterior',
    'Valor Nuevo',
    'Usuarios Afectados',
    'Archivos Subidos',
    'Cambio de Progreso (%)'
  ];

  // 2. Consolidar todas las entradas con información del control
  const allRows: string[][] = [];

  auditEntriesByControl.forEach(({ code, title, entries }) => {
    entries.forEach(entry => {
      const fecha = new Date(entry.timestamp).toLocaleString('es-ES');
      const usuario = entry.user.name;
      const email = entry.user.email;
      const accion = entry.action;
      const tipo = entry.actionType;
      const valorAnterior = entry.previousValue ? JSON.stringify(entry.previousValue) : '';
      const valorNuevo = entry.newValue ? JSON.stringify(entry.newValue) : '';
      const usuariosAfectados = entry.affectedUsers?.map(u => u.name).join(', ') || '';
      const archivosSubidos = entry.filesUploaded?.map(f => f.name).join(', ') || '';
      const cambioProgreso = entry.impactMetrics?.completionChange || '';

      allRows.push([
        `${code}: ${title}`,
        fecha,
        usuario,
        email,
        accion,
        tipo,
        valorAnterior,
        valorNuevo,
        usuariosAfectados,
        archivosSubidos,
        cambioProgreso.toString()
      ]);
    });
  });

  // 3. Construir CSV
  const csvContent = [
    [`# Auditoría Completa del Sistema`],
    [`# Exportado el ${new Date().toLocaleString('es-ES')}`],
    [`# Total de entradas: ${allRows.length}`],
    [''],
    headers,
    ...allRows
  ]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  // 4. Descargar
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `auditoria_completa_${Date.now()}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Exporta audit trail a formato PDF con tabla formateada
 * @param auditEntries - Array de entradas de auditoría
 * @param controlCode - Código del control (opcional, para el nombre del archivo)
 * @param controlTitle - Título del control (opcional, para el encabezado)
 */
export function exportAuditToPDF(
  auditEntries: AuditEntry[],
  controlCode?: string,
  controlTitle?: string
): void {
  // 1. Crear documento PDF
  const doc = new jsPDF();

  // 2. Agregar encabezado
  doc.setFontSize(18);
  doc.setTextColor(30, 58, 138); // Blue-900
  doc.text('Historial de Auditoría', 14, 20);

  // 3. Agregar información del control
  doc.setFontSize(12);
  doc.setTextColor(75, 85, 99); // Gray-600
  if (controlCode && controlTitle) {
    doc.text(`Control: ${controlCode} - ${controlTitle}`, 14, 30);
    doc.text(`Fecha de exportación: ${new Date().toLocaleString('es-ES')}`, 14, 37);
    doc.text(`Total de entradas: ${auditEntries.length}`, 14, 44);
  } else {
    doc.text(`Fecha de exportación: ${new Date().toLocaleString('es-ES')}`, 14, 30);
    doc.text(`Total de entradas: ${auditEntries.length}`, 14, 37);
  }

  // 4. Preparar datos para la tabla
  const tableData = auditEntries.map(entry => [
    new Date(entry.timestamp).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }),
    entry.user.name,
    entry.action,
    entry.actionType,
    entry.affectedUsers?.map(u => u.name).join(', ') || '-',
    entry.impactMetrics?.completionChange ? `${entry.impactMetrics.completionChange}%` : '-'
  ]);

  // 5. Crear tabla con jsPDF-AutoTable
  autoTable(doc, {
    startY: controlCode && controlTitle ? 50 : 44,
    head: [['Fecha', 'Usuario', 'Acción', 'Tipo', 'Afectados', 'Impacto']],
    body: tableData,
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    headStyles: {
      fillColor: [30, 58, 138], // Blue-900
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251] // Gray-50
    },
    margin: { top: 50 }
  });

  // 6. Agregar pie de página
  const pageCount = doc.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175); // Gray-400
    doc.text(
      `Página ${i} de ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // 7. Descargar PDF
  doc.save(`auditoria_${controlCode || 'completa'}_${Date.now()}.pdf`);
}
